import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import { useAppSelector } from '../../../../app/hooks'
import { AppDispatch } from '../../../../app/store'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  GET_OPD_SYSTEMIC_ENDPOINT,
  POST_OPD_SYSTEMIC_ENDPOINT,
} from '../../../../utils/Endpoints'
import {
  opdFormDataSaveApi,
  opdFormDataFetchApi,
} from '../../../../services/OpdFormsSTTService'
import { toastMessages, EXAMINATION_PHYSIO_TEXT_FIELDS } from '../constants'
import {
  selectExamination,
  isInitialLoadForOPDForms,
  setLoading,
  updateExamination,
  isSystemicExamOpdFormSubmitted,
  setIsSystemicExamOpdFormSubmit,
} from '../opdFormsSlice'

const styles = (theme: Theme) => ({
  textFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      minWidth: '250px',
      padding: '4px',
    },
  },
  fieldLabel: {
    textAlign: 'right',
    fontSize: '12px',
    textWrap: 'wrap',
    [theme.breakpoints.between('sm', 'md')]: {
      minWidth: '57px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '250px',
    },
  },
  textFieldWhiteBg: {
    backgroundColor: '#ffffff',
  },
  customFontBox: {
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
    },
    '& .MuiButton-contained': {
      backgroundColor: '#4682b4 !important',
      color: '#f1f3f5 !important',
      fontWeight: 600,
      border: '1px solid #4682b4 !important',
    },
  },
  submitButtonContainer: {
    [theme.breakpoints.up('md')]: {
      width: '73%',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '60%',
    },
  },
})

const ExaminationPhysio = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const { id } = useParams()
  const visitType = 'physio'
  const examination: any = useAppSelector(selectExamination)
  const dispatch = useDispatch<AppDispatch>()
  const [fhirRecordId, setFhirRecordId] = useState<string | null>(null)
  const isInitialLoadForForm = useAppSelector(isInitialLoadForOPDForms)
  const isFormSubmitted = useAppSelector(isSystemicExamOpdFormSubmitted)

  const initialValues = {
    palpation: examination?.palpation || '',
    reflexes: examination?.reflexes || '',
    rom: examination?.rom || '',
    muscleAssessment: examination?.muscleAssessment || '',
    jointAssessment: examination?.jointAssessment || '',
    relevantSpecialTests: examination?.relevantSpecialTests || '',
  }

  const validationSchema = Yup?.object({
    palpation: Yup?.string(),
    reflexes: Yup?.string(),
    rom: Yup?.string(),
    muscleAssessment: Yup?.string(),
    jointAssessment: Yup?.string(),
    relevantSpecialTests: Yup?.string(),
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleExaminationPhysioSubmitForm(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handleExaminationPhysioSubmitForm = async (
    data: Record<string, any>,
    setSubmitting: any
  ) => {
    setSubmitting(true)

    const payload = {
      encounterId: id,
      visitType: `opd/${visitType}`,
      fhirRecordId: fhirRecordId,
      ...data,
    }

    const response = await opdFormDataSaveApi(
      POST_OPD_SYSTEMIC_ENDPOINT,
      payload
    )

    if (response?.success) {
      dispatch(setIsSystemicExamOpdFormSubmit(true))
      toast.success(response?.message, { autoClose: 800 })
    } else {
      toast.error(response?.message || toastMessages.Form_Submission_Error, {
        autoClose: 800,
      })
    }
    setSubmitting(false)
  }

  const getExaminationPhysio = useCallback(async () => {
    if (!isInitialLoadForForm && !isFormSubmitted) return
    const getExaminationPhysioUrl = `${GET_OPD_SYSTEMIC_ENDPOINT}${id}&visitType=opd/${visitType}`

    const response = await opdFormDataFetchApi(getExaminationPhysioUrl)

    if (response?.success) {
      const result = response?.data
      const fhirRecordId = response?.data?.fhirRecordId
      setFhirRecordId(fhirRecordId)

      dispatch(
        updateExamination({
          palpation: result?.palpation,
          reflexes: result?.reflexes,
          rom: result?.rom,
          muscleAssessment: result?.muscleAssesment,
          jointAssessment: result?.jointAssessment,
          relevantSpecialTests: result?.relevantSpecialTests,
        })
      )
    } else {
      toast.error(toastMessages.Fetch_Failed)
    }
    dispatch(setLoading(false))
  }, [dispatch, id, visitType, isInitialLoadForForm, isFormSubmitted])

  useEffect(() => {
    getExaminationPhysio()
  }, [getExaminationPhysio])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'center'}
      gap={1}
      width={'fit-content'}
      className={classes?.customFontBox}
    >
      {EXAMINATION_PHYSIO_TEXT_FIELDS?.map(({ name, label }) => (
        <Box
          display={'flex'}
          justifyContent={'start'}
          alignItems={'center'}
          width={'100%'}
          key={name}
          flexWrap={'wrap'}
          gap={1}
        >
          <Typography className={classes?.fieldLabel}>{label}:</Typography>

          <TextField
            inputProps={{ 'data-testid': name }}
            multiline
            rows={4}
            variant='outlined'
            name={name}
            value={formik?.values[name as keyof typeof formik.values] || ''}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={
              formik?.touched[name as keyof typeof formik.values] &&
              Boolean(formik?.errors[name as keyof typeof formik.errors])
            }
            className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
          />
        </Box>
      ))}

      <Box
        display={'flex'}
        justifyContent={'end'}
        className={classes?.submitButtonContainer}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={formik?.submitForm}
          className={classes?.submitBtn}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default ExaminationPhysio
